import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { getSession } from "./utils/authHelpers";
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import Welcome from "./pages/Welcome/Welcome";
import Signupp from "./pages/Signupp/Signupp";
import TermsPage from "./pages/Terms/Terms";
import LoginPage from "./pages/Login/Login";
import CreateAcc from "./pages/CreateAcc/CreateAcc";
import VerifyAcc from "./pages/VerifyAcc/VerifyAcc";
import VerifyCode from "./pages/VerifyCode/VerifyCode";
import HomePage from "./pages/HomePage/HomePage";
import HowTo from "./pages/HowTo/HowTo";
import Stats from "./pages/Stats/Stats";
import History from "./pages/History/History";
import Callback from "./components/callback";

import { setContext } from "@apollo/client/link/context";
import PawLoader from "./components/PawLoader";
// import { refreshTokensIfNeeded } from "./utils/refreshTokens"; // optional

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
});

const authLink = setContext(() => {
  const token = localStorage.getItem("idToken") || localStorage.getItem("accessToken");
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// --- JWT helpers (no deps) ---
function decodeJwt(token) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}
function isJwtValid(token, skewSec = 60) {
  const p = decodeJwt(token);
  if (!p || typeof p.exp !== "number") return false;
  const now = Math.floor(Date.now() / 1000);
  return p.exp > now + skewSec;
}
function bestBearer() {
  const id = localStorage.getItem("idToken");
  const acc = localStorage.getItem("accessToken");
  return isJwtValid(id) ? id : isJwtValid(acc) ? acc : null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!(localStorage.getItem("idToken") || localStorage.getItem("accessToken"));
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // Optional: try to refresh with refresh_token first
      // await refreshTokensIfNeeded().catch(() => {});
      // 1) If we already have a valid JWT (SSO or password flow), trust it.
      const bearer = bestBearer();
      if (bearer) {
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      // 2) No valid JWTs — try Cognito SDK session (password-flow users).
      try {
        const cognitoSession = await getSession(); // may throw if no SDK session
        if (cognitoSession) {
          const idToken = cognitoSession.getIdToken()?.getJwtToken?.();
          const accessToken = cognitoSession.getAccessToken()?.getJwtToken?.();
          const refreshToken = cognitoSession.getRefreshToken()?.getToken?.();
          if (idToken) localStorage.setItem("idToken", idToken);
          if (accessToken) localStorage.setItem("accessToken", accessToken);
          if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (_e) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col md:flex-row h-screen bg-[#1A1A1A] text-white">
        <PawLoader />
      </div>
    );
  }

  return (
    <ApolloProvider client={client}>
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/welcome" />} />

          {/* Public Routes */}
          <Route
            path="/welcome"
            element={<Welcome setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route
            path="/login"
            element={<LoginPage setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route path="/create-account" element={<CreateAcc />} />
          <Route path="/verify-account" element={<VerifyAcc />} />
          <Route path="/verify" element={<VerifyCode />} />
          <Route
            path="/signupp"
            element={<Signupp setIsAuthenticated={setIsAuthenticated} />}
          />

          {/* Private Routes */}
          <Route
            path="/howto"
            element={isAuthenticated ? <HowTo /> : <Navigate to="/login" />}
          />
          <Route
            path="/home"
            element={isAuthenticated ? <HomePage /> : <Navigate to="/login" />}
          />
          <Route
            path="/stats"
            element={isAuthenticated ? <Stats /> : <Navigate to="/login" />}
          />
          <Route
            path="/history"
            element={isAuthenticated ? <History /> : <Navigate to="/login" />}
          />

          <Route
            path="/callback"
            element={<Callback setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route path="/terms" element={<TermsPage />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/welcome" />} />
        </Routes>
      </Router>
    </ApolloProvider>
  );
}

export default App;
